import { create } from 'zustand';
import {
  findShipmentsByReference,
  getTrackingActivationPreview,
  getTrackingActivations,
  type TrackingActivationShipment,
} from 'common/src/api/shipment-tracking-activation';

// Shipments listed in the panel per upload. The server activates all of
// them (queued by the upload itself), even after this page is closed.
export const MAX_IMMEDIATE_ACTIVATION = 40;
const POLL_MS = 1500;
// After this the panel stops watching; the server keeps going.
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

export interface ActivationItem {
  key: string;
  /** Container/AWB as uploaded, until the server answers. */
  label: string;
  reference: string;
  result: TrackingActivationShipment | null;
  pending: boolean;
}

export interface ActivationJob {
  id: number;
  items: ActivationItem[];
  /** Uploaded with a container/AWB but beyond the immediate limit. */
  deferred: number;
  running: boolean;
  preview: boolean;
}

export interface UploadedShipmentRow {
  reference_number?: string | null;
  container_number?: string | null;
  awb_number?: string | null;
}

interface TrackingActivationState {
  job: ActivationJob | null;
  start: (input: {
    exporterId: number;
    season: string;
    speciesId: number;
    rows: UploadedShipmentRow[];
  }) => Promise<void>;
  preview: () => Promise<void>;
  dismiss: () => void;
}

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';
const wait = (ms: number) => new Promise((done) => setTimeout(done, ms));

const useTrackingActivationStore = create<TrackingActivationState>(
  (set, get) => {
    let nextId = 1;
    const update = (
      jobId: number,
      change: (job: ActivationJob) => ActivationJob
    ) =>
      set((state) =>
        state.job?.id === jobId ? { job: change(state.job) } : state
      );
    const resolve = (
      jobId: number,
      keys: string[],
      results: TrackingActivationShipment[] | null
    ) =>
      update(jobId, (job) => ({
        ...job,
        items: job.items.map((item) => {
          if (!keys.includes(item.key)) return item;
          const result =
            results?.find((r) => String(r.shipment_id) === item.key) ?? null;
          return {
            ...item,
            pending: false,
            // Not finished yet: the server or the scheduled workers go on.
            result: result ?? {
              shipment_id: Number(item.key),
              identifier: item.label,
              mode: null,
              state: results ? 'no_identifier' : 'queued',
              journey_id: null,
              registration_opens_on: null,
              live_eta: null,
              destination_name: null,
              latest_event: null,
              has_route: false,
            },
          };
        }),
      }));

    return {
      job: null,
      dismiss: () => set({ job: null }),

      start: async ({ exporterId, season, speciesId, rows }) => {
        // Only rows that can be tracked: a container or an AWB.
        const byReference = new Map<string, string>();
        for (const row of rows) {
          const reference = text(row.reference_number);
          const identifier = text(row.container_number) || text(row.awb_number);
          if (reference && identifier && !byReference.has(reference))
            byReference.set(reference, identifier);
        }
        if (!byReference.size) return;

        const jobId = nextId++;
        const references = [...byReference.keys()];
        const immediate = references.slice(0, MAX_IMMEDIATE_ACTIVATION);
        set({
          job: {
            id: jobId,
            preview: false,
            running: true,
            deferred: references.length - immediate.length,
            items: immediate.map((reference) => ({
              key: `ref:${reference}`,
              label: byReference.get(reference)!,
              reference,
              result: null,
              pending: true,
            })),
          },
        });

        try {
          const shipments = await findShipmentsByReference(
            exporterId,
            season,
            speciesId,
            immediate
          );
          // Re-key items by shipment id once known.
          update(jobId, (job) => ({
            ...job,
            items: job.items.flatMap((item) => {
              const matches = shipments.filter(
                (s) => s.reference === item.reference
              );
              return matches.length
                ? matches.map((match) => ({ ...item, key: String(match.id) }))
                : [{ ...item, pending: false }];
            }),
          }));
          const ids = [...new Set(shipments.map((s) => s.id))];
          const deadline = Date.now() + POLL_TIMEOUT_MS;
          let first = true;
          while (ids.length && get().job?.id === jobId) {
            const rows = await getTrackingActivations(ids);
            if (get().job?.id !== jobId) return;
            if (first) {
              // Only shipments this upload queued (or activated before) stay:
              // a reference skipped unchanged starts nothing new.
              const queued = new Set(rows.map((r) => String(r.shipment_id)));
              update(jobId, (job) => ({
                ...job,
                items: job.items.filter(
                  (item) => !item.pending || queued.has(item.key)
                ),
              }));
              first = false;
            }
            const done = rows.flatMap((r) =>
              r.status === 'done' && r.result ? [r.result] : []
            );
            resolve(
              jobId,
              done.map((r) => String(r.shipment_id)),
              done
            );
            if (done.length === rows.length) break;
            if (Date.now() > deadline) {
              // Still running on the server; the panel just stops waiting.
              resolve(
                jobId,
                rows.flatMap((r) =>
                  r.status === 'done' ? [] : [String(r.shipment_id)]
                ),
                null
              );
              break;
            }
            await wait(POLL_MS);
          }
          // Nothing new to activate: no panel.
          if (get().job?.id === jobId && !get().job?.items.length)
            set({ job: null });
        } catch {
          // Could not look the shipments up: the scheduled workers take over.
          update(jobId, (job) => ({
            ...job,
            items: job.items.map((item) =>
              item.pending ? { ...item, pending: false } : item
            ),
          }));
        } finally {
          update(jobId, (job) => ({ ...job, running: false }));
        }
      },

      // Local preview with shipments already tracked (read-only, no credits):
      // replays what the panel shows as each shipment resolves.
      preview: async () => {
        const jobId = nextId++;
        let shipments: TrackingActivationShipment[];
        try {
          shipments = await getTrackingActivationPreview(8);
        } catch (error) {
          console.error('Tracking activation preview failed', error);
          return;
        }
        set({
          job: {
            id: jobId,
            preview: true,
            running: true,
            deferred: 0,
            items: shipments.map((s) => ({
              key: String(s.shipment_id),
              label: s.identifier ?? '—',
              reference: '',
              result: null,
              pending: true,
            })),
          },
        });
        for (const shipment of shipments) {
          await wait(700 + Math.random() * 900);
          if (get().job?.id !== jobId) return;
          resolve(jobId, [String(shipment.shipment_id)], [shipment]);
        }
        update(jobId, (job) => ({ ...job, running: false }));
      },
    };
  }
);

export default useTrackingActivationStore;
